/**
 * @file    chartCapabilities.ts
 * @scope   SHARED
 * @summary Capability tables describing what each chart alias supports (colour, trend, text…).
 *          Acts as a Strategy table so views ask "does this chart support X?" instead of
 *          branching through chains of `if (alias === ...)`.
 * @consumes `library` alias catalogue from `components`
 * @produces excludedChartTypes, chartsWithColor, chartsWithColorFamilies, chartsWithTrend,
 *           chartsWithTextControllers, dataGridRulesWithTrend
 * @sideEffects None (constant tables).
 */
import { library } from 'components';

/**
 * Chart types the DIY drawer must never offer, even if Guardrails returns them.
 * They are dashboard-level widgets that cannot be configured through the drawer.
 */
export const excludedChartTypes: string[] = [library.CORPORATE_PULSE, library.GEOMAP];

/** Charts rendered with a single accent colour → show the `ColorInput` picker. */
export const chartsWithColor: string[] = [
  library.VERTICAL_BAR_CHART,
  library.HORIZONTAL_BAR_CHART,
  library.BULLET_CHART,
];

/** Charts rendered with a palette (one colour per series) → show the `ColorFamilyInput` picker. */
export const chartsWithColorFamilies: string[] = [
  library.PIE_CHART,
  library.HORIZONTAL_SINGLE_STACKED_BAR_CHART,
  library.INDIVIDUAL_METRIC,
];

/** Charts whose data is time-bucketed → show the `SelectTrendInput` control. */
export const chartsWithTrend: string[] = [library.VERTICAL_BAR_CHART, library.STACKED_BAR_CHART];

/** Charts that are pure rich-text widgets → show `TextBlockControllers` instead of data pickers. */
export const chartsWithTextControllers: string[] = [library.TEXT_BLOCK];

/**
 * Business-rule names (not chart aliases) for DataGrid variants that still expose a trend picker.
 * WHY: DataGrid is a single chart type whose behaviour is driven by the selected rule, so the
 * trend capability has to be keyed by rule name rather than by alias.
 */
export const dataGridRulesWithTrend: string[] = ['OneQuestionOneCalculation'];
